import { apiClient, fetcher } from './apiClient';

/**
 * The shape of the API response
 */
type Organization = {
    id: string;
    name: string;
    createdAt: string;
    updateAt: string;
};

type OrgCounts = {
    locationCount: number;
    deviceTagCount: number;
    deviceCount: number;
};

type OrganizationSummary = {
    id: string;
    name: string;
    createdAt: string;
    updateAt: string;
    counts: OrgCounts;
};

const organizationFetcher = async (url: string): Promise<Organization> => {
    const organizationResponse = await fetcher<Organization>(url);

    return {
        id: organizationResponse.id,
        name: organizationResponse.name,
        createdAt: organizationResponse.createdAt,
        updateAt: organizationResponse.updateAt,
    } as Organization;
};

const organizationSummaryFetcher = async (url: string): Promise<OrganizationSummary> => {
    const organizationResponse = await fetcher<OrganizationSummary>(url);

    return {
        id: organizationResponse.id,
        name: organizationResponse.name,
        createdAt: organizationResponse.createdAt,
        updateAt: organizationResponse.updateAt,
        counts: organizationResponse.counts,
    } as OrganizationSummary;
};

/**
 * Reference counts that block deleting an org.
 *
 * These are the keys as they arrive, i.e. after ConvertEntityPropertiesToCamelCase
 * has rewritten the response — the backend's `device tags` reaches us as
 * `deviceTags`. Display text lives in COUNT_LABELS, not in the keys.
 */
type AdminOrgCounts = {
    users: number;
    locations: number;
    devices: number;
    deviceTags: number;
    scenes: number;
    components: number;
    playlists: number;
    /** Uploaded assets. Labelled "images" because that is all we accept today. */
    images: number;
};

type AdminOrganization = {
    id: string;
    name: string;
    avatarUrl: string | null;
    createdAt: string;
    counts: AdminOrgCounts;
};

const jsonHeaders = (csrfToken: string | null) => ({
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken ?? '',
});

/** Super-admin only: every org with the counts the admin UI needs. */
const listAdminOrganizations = async (): Promise<AdminOrganization[]> =>
    fetcher<AdminOrganization[]>('api/admin/organizations');

const createOrganization = async (
    csrfToken: string | null,
    name: string,
): Promise<Organization | undefined> => {
    if (csrfToken === null) return;
    const response = await apiClient.post(
        'api/organizations',
        JSON.stringify({ name }),
        { headers: jsonHeaders(csrfToken) },
    );

    return JSON.parse(response.data) as Organization;
};

/** Only succeeds for an empty org; otherwise the API returns a 422 explaining why. */
const deleteOrganization = async (
    csrfToken: string | null,
    orgId: string,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.delete(`api/admin/organizations/${orgId}`, {
        headers: jsonHeaders(csrfToken),
    });
};

export {
    createOrganization,
    deleteOrganization,
    listAdminOrganizations,
    organizationFetcher,
    organizationSummaryFetcher,
};
export type { AdminOrgCounts, AdminOrganization, Organization, OrganizationSummary };